import { useCallback, useEffect, useMemo, useState } from "react";
import { getTimeRange } from "../../Dashboard/Exchange3/Chart";
import FilterMenu from "../../../components/FilterMenu";
import { ChartAreaLoader } from "../../../components/Loading";
import {
  ChartDataParams,
  FormatChartData,
} from "../../../components/recharts/functions";
import {
  lightenColor,
  ReMixChart,
} from "../../../components/recharts/ReMixCharts";
import { BrushStart, LegendKey } from "../../../models/chart_model";
import { useLegendFilter } from "../../../hooks/useLegendFilter";
import { ExchangeColors } from "../../Dashboard/Exchange3/FormatData";
import { GROUP_BY, DataTabs } from "./Data";
import { renderHourTick } from "../../../components/recharts/components";
import LegendSummaryTable, {
  ChartTable,
  downloadChartTableCSV,
  downloadSummaryCSV,
} from "../../../components/table/SummaryTable";
import NewFilterContainer from "../../../components/NewFilterContainer ";
import MainPageContainer from "../../../components/MainPageContainer";
import SecondaryHeader from "../../Rajasthan/Demand/SecondaryHeader";
import Tooltip from "../../../components/tooltip";
import { useDispatch, useSelector } from "react-redux";
import BlockRangeSelector from "../../../components/BlockRangeSelector/BlockRangeSelector";
import { AddDatesWithFileName } from "../../../helpers/helper";
import { H_CHART_TYPES } from "../../Hppc/const";
import {
  getChhattisgarhDiscomData,
  getChhattisgarhForecastData,
  getTrendMetrics,
} from "../Utility/RestApi";
import { setLoadingFilterName } from "../../../store/state/FilterLoadingState";
import { MetricChipHead } from "../../../components/Metrics";
import { ReactComponent as PeakIcon } from "../../../icons/peakicon.svg";
import { ReactComponent as AvgIcon } from "../../../icons/AvgIcon.svg";
import { ReactComponent as MinIcon } from "../../../icons/MinIcon.svg";
import { ReactComponent as TotalIcon } from "../../../icons/TotalIcon.svg";
import { TrendData } from "../../../models/metrics";
// ─── Peak & Summary sub-views ────────────────────────────────────────────────
import { ChhattisgarhPeakAnalysis } from "./TrendPeak_Analysis";
import { ChhattisgarhSummaryAnalysis } from "./TrendSummary";
import { normalizeDisplayLabelWithoutUndefined } from "../../../utility/displayText";
import { formatDateForAPI } from "../const";

// ─── Intervals ───────────────────────────────────────────────────────────────
const Intervals = [
  {
    label: "Time Slot",
    key: "slot",
    values: ["block", "dateIndex"],
    isTime: true,
  },
  {
    label: "Date",
    key: "date",
    values: ["dateIndex"],
    isTime: false,
  },
  {
    label: "Month",
    key: "month",
    values: ["month"],
    isTime: false,
  },
];

const X_AXIS_MODES = [
  { label: "Intervals", value: "intervals" },
  { label: "Peak Analysis", value: "peak" },
  { label: "Summary", value: "summary" },
];
const DISCOM_OPTIONS = ["CSPDCL"];
const UNITS = ["MW", "MWh", "LU"];

// ─── Chart suffixes — single source of truth shared with sub-views ───────────
const CHART_SUFFIXES = [
  { suffix: "demand", displayName: "Actual Load", stackSuffix: "demand" },
  {
    suffix: "demand_forecast_d1",
    displayName: "Forecast D+1",
    stackSuffix: "forecast_d1",
  },
  {
    suffix: "demand_forecast_d2",
    displayName: "Forecast D+2",
    stackSuffix: "forecast_d2",
  },
  {
    suffix: "deviation",
    displayName: "Deviation %",
    stackSuffix: "deviation",
  },
  // {
  //   suffix: "deviation_value",
  //   displayName: "Deviation Value",
  //   stackSuffix: "deviation_value",
  // },
];

// ─── Helper ───────────────────────────────────────────────────────────────────
export function getMonthWithYear(dateString: string): string {
  const date = new Date(dateString);
  const monthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const month = monthNames[date.getMonth()];
  const year = date.getFullYear().toString().slice(-2);
  return `${month}-${year}`;
}

// ─── Component ────────────────────────────────────────────────────────────────
export function ChhattisgarhTrend() {
  const dispatch = useDispatch();
  const dataTab = useSelector((state: any) => state.viewMode.activeView);
  const activeTab = useSelector((state: any) => state.switchTabs.activeTab);
  const DefaultDates = useSelector(
    (state: any) => state.defaultDatesSetter.defaultDates,
  );
  const activeTabDates = useSelector(
    (state: any) => state.defaultDatesSetter.defaultDates?.[activeTab],
  );

  // ── State ─────────────────────────────────────────────────────────────────
  const [selectedXAxisMode, setSelectedXAxisMode] = useState(X_AXIS_MODES[0]);
  const [selectedUnit, setSelectedUnit] = useState(UNITS[0]);
  const [data, setData] = useState<any[]>([]);
  const [chartKeys, setChartKeys] = useState<any[]>([]);
  const [uniqueBlock, setUniqueBlock] = useState<any[]>([]);
  const [selectedBlock, setSelectedBlock] = useState<any[]>([]);
  const [selectedDiscom, setSelectedDiscom] = useState<string[]>([]);
  const [chartData, setChartData] = useState<any[]>([]);
  const [TableData, setTableData] = useState<any[]>([]);
  const [selectedChartType, setSelectedChartType] = useState(H_CHART_TYPES[1]);
  const [selectedInterval, setSelectedInterval] = useState(Intervals[0]);
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState<TrendData | null>(null);
  const [metricsLoading, setMetricsLoading] = useState(false);

  // Default: Actual Load + Forecast D+1 + Deviation % only.
  // Forecast D+2 and Deviation Value are hidden unless explicitly selected.
  function handleDiscomChange(items: string[]) {
    setSelectedDiscom(items);
  }
  const [selectedSuffix, setSelectedSuffix] = useState(
    CHART_SUFFIXES.filter((s) =>
      ["demand", "demand_forecast_d1", "deviation"].includes(s.suffix),
    ),
  );

  // ── Chart object builder ──────────────────────────────────────────────────
  const createChartObject = useCallback(
    (
      key: string,
      index: number,
      suffix: string,
      displayName: string,
      stackSuffix: string,
      _interval: { isTime: boolean },
      _chartType: { value: string; stackId?: string },
    ) => {
      const unit = selectedUnit.toLowerCase();

      const dataKey =
        suffix === "deviation"
          ? `${key}_deviation_${unit}`
          : suffix === "deviation_value"
            ? `${key}_deviation_value_${unit}`
            : suffix === "demand_forecast_d1" || suffix === "demand_forecast_d2"
              ? `${key}_demand_forecast_${unit}`
              : `${key}_${suffix}_${unit}`;

      // Base color per date-range index; Forecast is a lighter shade of the
      // same hue so Actual + Forecast for the same date are visually linked
      // (matches the Rajasthan TrendAnalysis pattern).
      const DEVIATION_COLORS = [
        "#00c8b4", // bright cyan-teal
        "#ff69b4", // hot pink
        "#adff2f", // chartreuse / yellow-green
        "#e040fb", // orchid / bright purple
        "#ffdd00", // bright yellow
        "#40c4ff", // light sky-blue
      ];

      const D2_COLORS = [
        "#ab47bc", // purple
        "#26c6da", // cyan
        "#d4e157", // lime
        "#ffa726", // amber
        "#ec407a", // pink
      ];

      const baseColor = ExchangeColors[index % ExchangeColors.length];
      const color =
        suffix === "demand"
          ? baseColor
          : suffix === "demand_forecast_d1"
            ? lightenColor(baseColor, 30)
            : suffix === "demand_forecast_d2"
              ? D2_COLORS[index % D2_COLORS.length]
              : suffix === "deviation" || suffix === "deviation_value"
                ? DEVIATION_COLORS[index % DEVIATION_COLORS.length]
                : "#ff9f40";

      const displayKey = normalizeDisplayLabelWithoutUndefined(key)
        .replace(/T\+1/g, "D+1")
        .replace(/T\+2/g, "D+2");

      const isDeviation =
        suffix === "deviation" || suffix === "deviation_value";

      // Build clean series name: "MAR-26 (Forecast D+1) Load" / "... Deviation %"
      let seriesName: string;
      if (
        suffix === "demand_forecast_d1" ||
        suffix === "demand_forecast_d2" ||
        suffix === "deviation" ||
        suffix === "deviation_value"
      ) {
        const baseDate = normalizeDisplayLabelWithoutUndefined(
          key.replace(/ \(Forecast\)/g, "").replace(/ \(T\+[12]\)$/, ""),
        ).trim();
        const fTypeMatch = key.match(/T\+([12])/);
        const fType = fTypeMatch
          ? `D+${fTypeMatch[1]}`
          : suffix === "demand_forecast_d2"
            ? "D+2"
            : "D+1";
        const metric =
          suffix === "demand_forecast_d1" || suffix === "demand_forecast_d2"
            ? "Load"
            : displayName; // "Deviation %" or "Deviation Value"
        seriesName = `${baseDate} (Forecast ${fType}) ${metric}`;
      } else {
        seriesName = `${displayKey} ${displayName}`;
      }

      return {
        dataKey,
        name: seriesName,
        yAxisId: suffix === "deviation" ? "right" : undefined,
        legendColor: color,
        stroke: color,
        type: _chartType.value,
        stackId:
          !isDeviation && selectedChartType.stackId !== undefined
            ? stackSuffix
            : undefined,
        ...(suffix === "deviation" && {
          unit: selectedUnit,
          deviationValueKey: `${key}_deviation_value_${unit}`,
        }),
        ...(suffix === "deviation_value" && {
          unit: selectedUnit,
        }),
      };
    },
    [selectedUnit, selectedChartType],
  );

  // ── Fetch data ────────────────────────────────────────────────────────────
  const _handlefetchData = useCallback(async () => {
    if (!activeTabDates?.defaultTrendDates) {
      setLoading(false);
      return;
    }
    setLoading(true);

    try {
      const trendDates = activeTabDates.defaultTrendDates;

      // Deduplicate by "startDate,endDate" — forecast entries share the same
      // date range as their actual counterpart (value = "start,end,Model 1")
      // so we skip them to avoid duplicate API calls.
      const seenRanges = new Set<string>();
      const uniqueTrendDates = trendDates.filter((date: any) => {
        const [start, end] = date.value.split(",");
        const rangeKey = `${start},${end}`;
        if (seenRanges.has(rangeKey)) return false;
        seenRanges.add(rangeKey);
        return true;
      });

      const promises = uniqueTrendDates.map(async (date: any) => {
        try {
          const [startDate, endDate] = date.value.split(",");

          // Fetch actual load and forecast data from separate APIs in parallel
          const [actualItems, forecastItems] = await Promise.all([
            getChhattisgarhDiscomData(startDate, endDate),
            getChhattisgarhForecastData(startDate, endDate),
          ]);

          const uniqueDates = Array.from(
            new Set(
              [
                ...actualItems.map((i: any) => i.date),
                ...forecastItems.map((i: any) => i.date),
              ].filter(Boolean),
            ),
          ).sort();
          const actualRows = actualItems.map((item: any) => {
            const monthVal = item.date
              ? getMonthWithYear(item.date)
              : "Unknown";
            const dayPos = uniqueDates.indexOf(item.date);
            const dateIndex = dayPos >= 0 ? `${dayPos + 1} Day` : "1 Day";
            return {
              ...item,
              dates: date.label,
              month: monthVal,
              dateIndex,
              demand_forecast_mw: 0,
              demand_forecast_mwh: 0,
              demand_forecast_lu: 0,
            };
          });

          // Forecast rows (D+1 / D+2) — from getChhattisgarhForecastData
          const forecastRows = forecastItems.map((item: any) => {
            const monthVal = item.date
              ? getMonthWithYear(item.date)
              : "Unknown";
            const dayPos = uniqueDates.indexOf(item.date);
            const dateIndex = dayPos >= 0 ? `${dayPos + 1} Day` : "1 Day";
            const fType = item.forecast_type || "T+1";
            return {
              ...item,
              dates: `${date.label} (${fType})`,
              month: monthVal,
              dateIndex,
              demand_mw: 0,
              demand_mwh: 0,
              demand_lu: 0,
              demand_forecast_mwh: (item.demand_forecast_mw || 0) / 4,
              demand_forecast_lu: (item.demand_forecast_mw || 0) / 4 / 100,
            };
          });

          return [...actualRows, ...forecastRows];
        } catch (err) {
          console.error(`Error fetching date ${date.label}:`, err);
          return [];
        }
      });

      const results = await Promise.all(promises);
      const newData = results.flat();

      // Deduplicate by dates + date + block
      const seen = new Set<string>();
      const deduped = newData.filter((item) => {
        const key = `${item.dates}_${item.date}_${item.block}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });

      const sorted =
        selectedInterval.key === "month"
          ? [...deduped].sort(
              (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
            )
          : deduped;

      setData(sorted);
      setUniqueBlock(Array.from(new Set(sorted.map((i) => i.block))));
    } catch (err) {
      console.error("Error in data fetching:", err);
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [activeTabDates, selectedInterval.key]);

  useEffect(() => {
    _handlefetchData();
  }, [_handlefetchData]);
  const fetchMetrics = useCallback(async () => {
    if (!activeTabDates?.defaultTrendDates) {
      setMetrics(null);
      setMetricsLoading(false);
      return;
    }
    setMetricsLoading(true);

    try {
      const originalDates = activeTabDates.defaultTrendDates;

      const ranges = originalDates
        .filter((date: any) => {
          // Skip forecast entries — value has 3 parts: "start,end,Model 1"
          const commaParts = date.value.split(",");
          return commaParts.length <= 2;
        })
        .map((date: any) => {
          let start: string;
          let end: string;

          if (date.value.includes(" to ")) {
            [start, end] = date.value
              .split(" to ")
              .map((s: string) => s.trim());
          } else {
            const parts = date.value.split(",").map((s: string) => s.trim());
            start = parts[0];
            end = parts[1] ?? parts[0];
          }

          return {
            startDate: formatDateForAPI(new Date(start)),
            endDate: formatDateForAPI(new Date(end)),
            blocks: selectedBlock.map((block: any) => block.value),
          };
        })
        .filter((range: any) => range.startDate && range.endDate);
      if (ranges.length === 0) {
        setMetrics(null);
        setMetricsLoading(false);
        return;
      }
      // Send only forecast types — the backend uses these to determine which
      // forecast model metrics to return (D+1 and/or D+2)
      const selectedTypes = selectedSuffix
        .map((s) => s.displayName)
        .filter((s) => s === "Forecast D+1");

      const metricsData = await getTrendMetrics({
        ranges,
        aggregated: true,
        selectedTypes,
      });

      setMetrics(Array.isArray(metricsData) ? metricsData[0] : metricsData);
    } catch (error) {
      console.error("Error fetching metrics:", error);
      setMetrics(null);
    } finally {
      setMetricsLoading(false);
    }
  }, [activeTabDates.defaultTrendDates, selectedBlock, selectedSuffix]);

  useEffect(() => {
    fetchMetrics();
  }, [fetchMetrics]);

  // ── Format chart data ─────────────────────────────────────────────────────
  useEffect(() => {
    let filteredData = [...data];

    if (selectedBlock.length > 0) {
      filteredData = filteredData.filter((item) =>
        selectedBlock.map((o) => o.value).includes(item.block),
      );
    }

    filteredData.sort((a, b) => {
      const d = new Date(a.date).getTime() - new Date(b.date).getTime();
      return d !== 0 ? d : a.block - b.block;
    });

    function getKeys() {
      if (selectedInterval.key === "slot") return ["block", "dateIndex"];
      if (selectedInterval.key === "date") return ["dateIndex"];
      return ["month"];
    }

    const aggregationKeys = [
      `demand_mw`,
      `demand_mwh`,
      `demand_lu`,
      `demand_forecast_mw`,
      `demand_forecast_mwh`,
      `demand_forecast_lu`,
    ];

    const par: ChartDataParams = {
      keys: getKeys(),
      groupBy: ["dates"],
      tableKeys: ["dateIndex", "block", "month"],
      volumeKey: "",
      aggregationKeys,
      averageKeys: aggregationKeys,
      averageDuplicationKeys: ["dateIndex", "block", "month"],
      priceKey: "",
      data: filteredData,
    };

    const cData = FormatChartData(par);

    // Build a lookup so we can restore the actual date on each aggregated row
    const dateByBlockDay = new Map<string, string>();
    filteredData.forEach((item) => {
      const key = `${item.block}_${item.dateIndex}`;
      if (!dateByBlockDay.has(key)) dateByBlockDay.set(key, item.date);
    });

    const unit = selectedUnit.toLowerCase();

    const processedData = cData.data.map((dataPoint) => {
      const newDataPoint = { ...dataPoint };
      const lookupKey = `${dataPoint.block}_${dataPoint.dateIndex}`;
      if (dateByBlockDay.has(lookupKey)) {
        newDataPoint.date = dateByBlockDay.get(lookupKey);
      }
      cData.keys.forEach((key) => {
        // Forecast key (T+1 / T+2): compute deviation vs matching actual key
        const isForecastKey = / \(T\+[12]\)$/.test(key);
        if (isForecastKey) {
          const actualDateKey = key.replace(/ \(T\+[12]\)$/, "");
          const actualDataKey = `${actualDateKey}_demand_${unit}`;
          const forecastDataKey = `${key}_demand_forecast_${unit}`;

          const actual = dataPoint[actualDataKey] ?? 0;
          const forecast = dataPoint[forecastDataKey] ?? 0;

          if (forecast === 0) {
            newDataPoint[forecastDataKey] = null;
            newDataPoint[`${key}_deviation_${unit}`] = null;
            newDataPoint[`${key}_deviation_value_${unit}`] = null;
          } else {
            const deviation =
              actual !== 0 ? (Math.abs(actual - forecast) / actual) * 100 : 0;
            newDataPoint[`${key}_deviation_${unit}`] = deviation;
            newDataPoint[`${key}_deviation_value_${unit}`] = Math.abs(
              actual - forecast,
            );
          }
        } else {
          // Actual key: null out if zero
          const actualKey = `${key}_demand_${unit}`;
          const actual = dataPoint[actualKey] ?? 0;
          if (actual === 0) {
            newDataPoint[actualKey] = null;
          }
          newDataPoint[`${key}_deviation_${unit}`] = null;
          newDataPoint[`${key}_deviation_value_${unit}`] = null;
        }
      });
      return newDataPoint;
    });

    setChartData(processedData);
    setChartKeys([...cData.keys].sort());
    setTableData(cData.tableData || []);
  }, [data, selectedInterval, selectedBlock, selectedSuffix, selectedUnit]);

  // ── Legends ───────────────────────────────────────────────────────────────
  const legends = useMemo(
    () =>
      selectedSuffix.flatMap(({ suffix, displayName, stackSuffix }) => {
        // Actual demand: exclude forecast-labelled keys and T+1/T+2 keys
        // Forecast D+1/D+2: prefer "(Forecast)" keys; fall back to plain T+1/T+2 if none
        const dedupeForecasts = (keys: string[], tPattern: RegExp) => {
          const hasForecast = keys.some((k) => /\(Forecast\)/.test(k));
          return hasForecast
            ? keys.filter((k) => /\(Forecast\)/.test(k))
            : keys;
        };

        const filteredKeys =
          suffix === "demand"
            ? chartKeys.filter(
                (key: string) =>
                  !/ \(T\+[12]\)$/.test(key) && !/\(Forecast\)/.test(key),
              )
            : suffix === "demand_forecast_d1"
              ? dedupeForecasts(
                  chartKeys.filter((key: string) => / \(T\+1\)$/.test(key)),
                  / \(T\+1\)$/,
                )
              : suffix === "demand_forecast_d2"
                ? dedupeForecasts(
                    chartKeys.filter((key: string) => / \(T\+2\)$/.test(key)),
                    / \(T\+2\)$/,
                  )
                : (() => {
                    // Only show deviation for forecast types that are selected
                    const d1Selected = selectedSuffix.some(
                      (s) => s.suffix === "demand_forecast_d1",
                    );
                    const d2Selected = selectedSuffix.some(
                      (s) => s.suffix === "demand_forecast_d2",
                    );
                    const deviationKeys = chartKeys.filter((key: string) => {
                      if (/ \(T\+1\)$/.test(key)) return d1Selected;
                      if (/ \(T\+2\)$/.test(key)) return d2Selected;
                      return false;
                    });
                    return dedupeForecasts(deviationKeys, / \(T\+[12]\)$/);
                  })();
        return filteredKeys.map((key, index) =>
          createChartObject(
            key,
            index,
            suffix,
            displayName,
            stackSuffix,
            selectedInterval,
            selectedChartType as any,
          ),
        );
      }),
    [
      selectedSuffix,
      chartKeys,
      selectedInterval,
      selectedChartType,
      createChartObject,
    ],
  );

  // ── Handlers ──────────────────────────────────────────────────────────────
  function handleUnitChange(unit: string) {
    setSelectedUnit(unit);
  }

  function handleXAxisModeChange(_item: string, index: number) {
    setSelectedXAxisMode(X_AXIS_MODES[index]);
  }

  /**
   * handleTypeChange — updates selectedSuffix in this component.
   * Because selectedSuffix is passed down to Peak/Summary sub-views as a prop,
   * changing it here automatically reflects in the sub-view's Type filter.
   */
  function handleTypeChange(selected: string[]) {
    setSelectedSuffix(
      CHART_SUFFIXES.filter((s) => selected.includes(s.displayName)),
    );
    dispatch(setLoadingFilterName(""));
  }

  function getSelectedTypes() {
    return selectedSuffix.map((s) => s.displayName);
  }

  const xDataKey = useMemo(() => {
    if (selectedInterval.isTime) return "block";
    return selectedInterval.values[0];
  }, [selectedInterval]);

  const secondXDataKey = useMemo(() => {
    if (selectedInterval.isTime) return "dateIndex";
    return undefined;
  }, [selectedInterval.isTime]);

  // ── Custom tooltip: 3 lines with deviation value in parens ────────────────
  const customTooltip = useCallback(
    (params: any) => {
      const items: any[] = Array.isArray(params) ? params : [params];
      const param = items[0];
      const dataPoint = chartData[param.dataIndex] || {};
      const unit = selectedUnit.toLowerCase();

      let title = param.name;
      if (secondXDataKey && dataPoint[secondXDataKey]) {
        title = selectedInterval.isTime
          ? `${dataPoint[secondXDataKey]} - ${param.name} (${getTimeRange(parseInt(param.name))})`
          : `${dataPoint[secondXDataKey]} - ${param.name}`;
      } else if (selectedInterval.isTime) {
        title = getTimeRange(parseInt(param.name)) + ` (${param.name})`;
      }

      const validItems = items.filter((item) => {
        if (!item.seriesName || item.seriesName.startsWith("__")) return false;
        if (item.value === null || item.value === undefined) return false;
        const v = parseFloat(item.value);
        return !isNaN(v) && isFinite(v);
      });

      const tooltipItems = validItems.map((item) => {
        const isDeviationPct = (item.seriesName as string).includes(
          "Deviation %",
        );
        const numVal = parseFloat(item.value);
        const formattedVal = numVal
          .toFixed(2)
          .replace(/\B(?=(\d{3})+(?!\d))/g, ",");

        let displayValue: string;
        if (isDeviationPct) {
          const displayKeyFromName = (item.seriesName as string).replace(
            / Deviation %$/,
            "",
          );
          // New format "MAR-26 (Forecast D+1)" → multiple fallback lookups
          // 1. With (Forecast) prefix: "MAR-26 (Forecast) (D+1)"
          const expandedKey = displayKeyFromName.replace(
            /\s*\(Forecast\s+(D\+[12])\)/,
            " (Forecast) ($1)",
          );
          // 2. Without (Forecast) prefix: "MAR-26 (D+1)" — for plain date labels
          const simpleKey = displayKeyFromName.replace(
            /\s*\(Forecast\s+(D\+[12])\)/,
            " ($1)",
          );
          const normalize = (k: string) =>
            normalizeDisplayLabelWithoutUndefined(k)
              .replace(/T\+1/g, "D+1")
              .replace(/T\+2/g, "D+2");
          const matchingKey =
            chartKeys.find((k) => normalize(k) === expandedKey) ||
            chartKeys.find((k) => normalize(k) === simpleKey) ||
            chartKeys.find((k) => normalize(k) === displayKeyFromName) ||
            displayKeyFromName;
          const devVal = dataPoint[`${matchingKey}_deviation_value_${unit}`];
          if (devVal !== null && devVal !== undefined) {
            const devNum = parseFloat(devVal);
            const devFormatted = Math.abs(devNum)
              .toFixed(2)
              .replace(/\B(?=(\d{3})+(?!\d))/g, ",");
            displayValue = `(${devFormatted} ${selectedUnit}) ${formattedVal} %`;
          } else {
            displayValue = `${formattedVal} %`;
          }
        } else {
          displayValue = `${formattedVal} ${selectedUnit}`;
        }

        return `
    <div class="tooltip-item" data-name="${(item.seriesName as string).toLowerCase()}">
      <div class="color-indicator" style="background-color: ${item.color}"></div>
      <div class="series-name">${item.seriesName}</div>
      <div class="value">${displayValue}</div>
    </div>`;
      });

      return `<div class="enhanced-tooltip"><div class="tooltip-header"><div class="tooltip-title">${title}</div></div><div class="tooltip-content">${tooltipItems.join("")}</div></div><style>.enhanced-tooltip{font-family:'Inter',system-ui,sans-serif;width:100%;min-width:200px}.tooltip-header{display:flex;flex-direction:column;gap:8px;border-bottom:1px solid #eaeaea;padding-bottom:8px;margin-bottom:8px}.tooltip-title{font-weight:600;font-size:13px;color:#333}.tooltip-content{max-height:300px;overflow-y:auto;padding-right:10px}.tooltip-item{display:flex;align-items:center;padding:4px 0;border-bottom:1px solid #f0f0f0}.tooltip-item:last-child{border-bottom:none}.color-indicator{width:10px;height:10px;border-radius:50%;margin-right:8px}.series-name{flex:1;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;font-size:12px}.value{font-weight:500;margin-left:10px;font-size:12px}</style>`;
    },
    [chartData, chartKeys, selectedUnit, secondXDataKey, selectedInterval],
  );

  function summaryExtraKeys() {
    if (selectedInterval.isTime) return ["block", "dateIndex"];
    if (selectedInterval.key === "date") return ["dateIndex"];
    return [selectedInterval.values[0]];
  }

  const summaryExtraLabels = (() => {
    return selectedInterval.values.map((v) => (v === "dateIndex" ? "Day" : v));
  })();

  const { visibleLegends, handleLegendSelect, selectedLegends } =
    useLegendFilter(legends as any);

  const commonSummaryProps = {
    legends: visibleLegends,
    data: chartData,
    tableData: TableData,
    unit: selectedUnit,
    extraKeys: summaryExtraKeys(),
    extraLabels: summaryExtraLabels,
    filename: AddDatesWithFileName({
      fileName: "Forecast Load",
      dates: DefaultDates[activeTab]?.defaultTrendDates || [],
    }),
  };

  // ── Props forwarded to Peak / Summary sub-views ───────────────────────────
  // selectedSuffix is passed as-is so the sub-view's Type filter always
  // mirrors whatever is selected in the main Intervals view.
  const sharedSubViewProps = {
    onXAxisModeChange: handleXAxisModeChange,
    selectedUnit,
    onUnitChange: handleUnitChange,
    selectedDiscom,
    onDiscomChange: handleDiscomChange,
    selectedGroupKeys: "Discom Load",
    onGroupByChange: () => {},
    selectedSuffix, // ← live reference; sub-view re-renders on change
    onTypeChange: handleTypeChange, // ← sub-view calls this to update parent
    chartSuffixes: CHART_SUFFIXES,
    metrics,
  };

  // ── Peak / Summary routes ─────────────────────────────────────────────────
  if (selectedXAxisMode.value === "peak") {
    return <ChhattisgarhPeakAnalysis {...sharedSubViewProps} />;
  }

  if (selectedXAxisMode.value === "summary") {
    return <ChhattisgarhSummaryAnalysis {...sharedSubViewProps} />;
  }

  // ── Main Intervals render ─────────────────────────────────────────────────
  return (
    <div className="gas-page-container">
      <NewFilterContainer>
        <div className="side-filters-container text-nowrap">
          <FilterMenu
            title="X-Axis"
            items={X_AXIS_MODES.map((i) => i.label)}
            selectedItems={[selectedXAxisMode.label]}
            onSelect={handleXAxisModeChange}
          />

          <FilterMenu
            title="Intervals"
            items={Intervals.map((i) => i.label)}
            selectedItems={[selectedInterval.label]}
            onSelect={(_item, index) => setSelectedInterval(Intervals[index])}
          />

          <FilterMenu
            title="Unit"
            items={UNITS}
            selectedItems={[selectedUnit]}
            onSelect={(item) => handleUnitChange(item)}
          />
          {/* <FilterMenu
                      title="Discom"
                      items={DISCOM_OPTIONS}
                      selectedItems={selectedDiscom}
                      onSelect={() => {}}
                      setSelectedItems={handleDiscomChange}
                      // disabled={true}
                    /> */}

          {/* Type (Actual Load / Forecast Load / Deviation %) */}
          <FilterMenu
            title="Type"
            items={CHART_SUFFIXES.map((s) => s.displayName)}
            selectedItems={getSelectedTypes()}
            setSelectedItems={handleTypeChange}
          />

          <FilterMenu
            title="Chart Type"
            items={H_CHART_TYPES.map((i) => i.label)}
            selectedItems={[selectedChartType.label]}
            onSelect={(_item, index) =>
              setSelectedChartType(H_CHART_TYPES[index])
            }
          />
        </div>
      </NewFilterContainer>

      <MainPageContainer>
        <SecondaryHeader />
        {metrics && (
          <div className="flex flex-wrap gap-4 my-0 justify-center">
            <MetricChipHead
              icon={<PeakIcon width={20} height={19} />}
              title="Peak Load"
              items={[
                {
                  label: "Actual",
                  value: metrics.load_metrics["Peak Load (MW)"].value,
                  unit: metrics.load_metrics["Peak Load (MW)"].unit,
                },
                {
                  label: "Forecast",
                  value:
                    metrics.forecast_metrics["Peak Forecast Load (MW)"].value,
                  unit: metrics.forecast_metrics["Peak Forecast Load (MW)"]
                    .unit,
                },
              ]}
            />
            <MetricChipHead
              icon={<AvgIcon width={20} height={19} />}
              title="Average Load"
              items={[
                {
                  label: "Actual",
                  value: metrics.load_metrics["Average Load (MW)"].value,
                  unit: metrics.load_metrics["Average Load (MW)"].unit,
                },
                {
                  label: "Forecast",
                  value:
                    metrics.forecast_metrics["Average Forecast Load (MW)"]
                      .value,
                  unit: metrics.forecast_metrics["Average Forecast Load (MW)"]
                    .unit,
                },
              ]}
            />
            <MetricChipHead
              icon={<MinIcon width={20} height={19} />}
              title="Minimum Load"
              items={[
                {
                  label: "Actual",
                  value: metrics.load_metrics["Minimum Load (MW)"].value,
                  unit: metrics.load_metrics["Minimum Load (MW)"].unit,
                },
                {
                  label: "Forecast",
                  value:
                    metrics.forecast_metrics["Minimum Forecast Load (MW)"]
                      .value,
                  unit: metrics.forecast_metrics["Minimum Forecast Load (MW)"]
                    .unit,
                },
              ]}
            />
            <MetricChipHead
              icon={<TotalIcon width={20} height={19} />}
              title="Total Energy"
              items={[
                {
                  label: "Actual",
                  value: metrics.load_metrics["Total Energy (MU)"].value,
                  unit: metrics.load_metrics["Total Energy (MU)"].unit,
                },
                {
                  label: "Forecast",
                  value:
                    metrics.forecast_metrics["Total Forecast Energy (MU)"]
                      .value,
                  unit: metrics.forecast_metrics["Total Forecast Energy (MU)"]
                    .unit,
                },
              ]}
            />
          </div>
        )}
        <div className="gas-chart-container">
          {loading ? (
            <ChartAreaLoader
              title="Loading Trend Analysis"
              subtitle="Fetching Chhattisgarh demand trend analysis data…"
            />
          ) : (
            <>
              <div className="gas-heading gap-x-10">
                <h2 className="chartHeading w-72">
                  Forecast Load ({selectedUnit})
                </h2>

                <div className="flex gap-x-6 w-full justify-end">
                  <BlockRangeSelector
                    blocks={Array.from({ length: 96 }, (_, i) => i + 1)}
                    placeholder="Search Block"
                    selectedOptions={selectedBlock.map((i) => i.value)}
                    setSelectedOptions={setSelectedBlock}
                  />
                  <button
                    onClick={() => {
                      if (dataTab.label === "Chart")
                        downloadChartTableCSV(commonSummaryProps);
                      if (dataTab.label === "Summary")
                        downloadSummaryCSV(commonSummaryProps);
                      if (dataTab.label === "Table")
                        downloadChartTableCSV(commonSummaryProps);
                    }}
                    className="downloadButton"
                  >
                    Download Data
                  </button>
                </div>
              </div>
              {chartData.length === 0 && !loading && (
                <div
                  style={{
                    padding: "40px",
                    textAlign: "center",
                    color: "#666",
                  }}
                >
                  No data available for the selected dates.
                </div>
              )}

              {dataTab.label === "Chart" && (
                <ReMixChart
                  data={chartData}
                  showBrush={chartData.length > 96 ? true : undefined}
                  brushStart={BrushStart.Start}
                  isTimeSlot={selectedInterval.isTime}
                  onlyTitle={true}
                  barCategoryGap={10}
                  secondYAxisDomain={[0, 100]}
                  secondYAxisLabel={
                    selectedSuffix.some((s) => s.suffix === "deviation")
                      ? "Deviation %"
                      : undefined
                  }
                  unit={selectedUnit}
                  yAxisLabel={selectedUnit}
                  legends={legends as any}
                  defaultSelectedLegends={selectedLegends}
                  onLegendSelect={handleLegendSelect}
                  key={`${selectedUnit}-${selectedInterval.key}-${selectedChartType.value}`}
                  xDataKey={xDataKey}
                  secondXDataKey={secondXDataKey}
                  xTick={
                    selectedInterval.isTime
                      ? (value: any, index: any) => renderHourTick(value, 10)
                      : undefined
                  }
                  customTooltip={customTooltip}
                />
              )}

              {dataTab.label === "Summary" && (
                <LegendSummaryTable {...commonSummaryProps} />
              )}

              {dataTab.label === "Table" && (
                <ChartTable {...commonSummaryProps} />
              )}
            </>
          )}
        </div>

        <div className="gna-footer-container">
          <div className="grid grid-cols-3 justify-between gap-5">
            <Tooltip
              note={<p>CSPDCL</p>}
              text="
    1. Actual Load data is sourced from CSPDCL's official records.
    2. Forecast Load data is provided by GNA Energy."
            >
              <p className="gna-footer-text">Source</p>
            </Tooltip>
            <p className="gna-footer-text col-span-1 text-center">
              Powered by <strong>GNA Energy</strong>
            </p>
            <strong className="gna-footer-text col-span-1 text-end">
              Beta Version
            </strong>
          </div>
        </div>
      </MainPageContainer>
    </div>
  );
}
